<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">VPN - Bảo mật mạng</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">VPN - Bảo mật mạng và bảo vệ dữ liệu cá nhân 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>12/12/2024</span>
          <span class="mx-2">•</span>
          <span>7 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Bảo mật mạng</span>
        </div>
        <div class="h-64 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
          <svg class="w-24 h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-8">
        <div class="prose prose-lg max-w-none">
          <h2 class="mb-2 text-3xl font-bold text-gray-900">I. VPN là gì?</h2>
          <p>
            <strong>VPN (Virtual Private Network)</strong> là một công cụ giúp kết nối an toàn và ẩn danh khi bạn truy cập Internet, 
            thay đổi IP public của nhà mạng (IP private của thiết bị không bị ảnh hưởng).
          </p>
          <p>
            VPN tạo ra một kết nối mạng riêng ảo, cho phép bạn truy cập internet một cách an toàn và riêng tư. 
            VPN mã hóa dữ liệu của bạn và định tuyến qua các server từ xa, bảo vệ thông tin cá nhân khỏi các mối đe dọa mạng.
          </p>
          <div class="bg-blue-50 p-4 rounded-lg my-6">
            <p class="text-blue-800 font-semibold mb-2">💡 Lưu ý quan trọng:</p>
            <p class="text-blue-700">
              VPN chỉ thay đổi <strong>IP public của nhà mạng</strong>, 
              <strong>IP private của thiết bị không bị ảnh hưởng</strong>.
            </p>
          </div>

          <h2 class="mb-2 text-3xl font-bold text-gray-900">II. Cách hoạt động của VPN</h2>
          
          <h3>1. Địa chỉ IP của bạn được ẩn và thay bằng IP của máy chủ VPN</h3>
          <p>Khi kết nối VPN, địa chỉ IP thực của bạn sẽ bị ẩn và thay thế bằng địa chỉ IP của server VPN.</p>

          <h3>2. Luồng dữ liệu</h3>
          
          <div class="space-y-4">
            <div class="bg-red-50 p-4 rounded-lg">
              <h4 class="font-semibold text-red-800 mb-2">❌ Không VPN:</h4>
              <p class="text-red-700"><strong>Luồng:</strong> Thiết bị → Modem → Internet (với IP thực của bạn)</p>
            </div>

            <div class="bg-green-50 p-4 rounded-lg">
              <h4 class="font-semibold text-green-800 mb-2">✅ Có VPN:</h4>
              <p class="text-green-700"><strong>Luồng:</strong> Thiết bị → Máy chủ VPN (IP của máy chủ VPN) → Internet</p>
            </div>
          </div>

          <p class="mt-4">
            Khi bạn kết nối VPN, thiết bị của bạn sẽ:
          </p>
          <ul>
            <li>• Tạo một "đường hầm" mã hóa đến server VPN</li>
            <li>• Mã hóa tất cả dữ liệu trước khi gửi đi</li>
            <li>• Chuyển tiếp dữ liệu qua server VPN</li>
            <li>• Giải mã dữ liệu khi nhận về</li>
          </ul>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">III. Lợi ích của VPN</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-2">🌍 Thay đổi địa chỉ IP</h3>
              <ul class="text-blue-700 space-y-1">
                <li>• Giúp bạn truy cập trang web từ quốc gia khác</li>
                <li>• Vượt qua giới hạn địa lý</li>
                <li>• Xem nội dung bị chặn theo vùng</li>
              </ul>
            </div>

            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">🔒 Bảo mật</h3>
              <ul class="text-green-700 space-y-1">
                <li>• Dữ liệu được mã hóa</li>
                <li>• Khó bị theo dõi hoặc đánh cắp</li>
                <li>• Bảo vệ thông tin nhạy cảm</li>
              </ul>
            </div>

            <div class="bg-purple-50 p-4 rounded-lg">
              <h3 class="font-semibold text-purple-800 mb-2">🕵️ Ẩn danh</h3>
              <ul class="text-purple-700 space-y-1">
                <li>• Giấu địa chỉ IP thật của bạn</li>
                <li>• Bảo vệ quyền riêng tư</li>
                <li>• Duyệt web ẩn danh</li>
              </ul>
            </div>

            <div class="bg-yellow-50 p-4 rounded-lg">
              <h3 class="font-semibold text-yellow-800 mb-2">📶 Bảo vệ WiFi công cộng</h3>
              <ul class="text-yellow-700 space-y-1">
                <li>• Sử dụng WiFi công cộng an toàn</li>
                <li>• Bảo vệ khỏi tấn công man-in-the-middle</li>
                <li>• Mã hóa tất cả lưu lượng</li>
              </ul>
            </div>
          </div>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">IV. Các loại VPN phổ biến</h2>
          
          <div class="space-y-6">
            <div class="border-l-4 border-green-500 pl-4">
              <h3>🆓 VPN miễn phí</h3>
              <ul class="ml-4 space-y-1">
                <li>• <strong>Ưu điểm:</strong> Không tốn chi phí</li>
                <li>• <strong>Nhược điểm:</strong> Tốc độ chậm, giới hạn băng thông, có thể bán dữ liệu</li>
              </ul>
            </div>

            <div class="border-l-4 border-blue-500 pl-4">
              <h3>💳 VPN trả phí</h3>
              <ul class="ml-4 space-y-1">
                <li>• <strong>Ưu điểm:</strong> Tốc độ cao, bảo mật tốt, hỗ trợ 24/7</li>
                <li>• <strong>Nhược điểm:</strong> Cần trả phí hàng tháng/năm</li>
              </ul>
            </div>

            <div class="border-l-4 border-purple-500 pl-4">
              <h3>🏢 VPN doanh nghiệp</h3>
              <ul class="ml-4 space-y-1">
                <li>• <strong>Ưu điểm:</strong> Bảo mật cao, quản lý tập trung</li>
                <li>• <strong>Nhược điểm:</strong> Chi phí cao, phức tạp trong triển khai</li>
              </ul>
            </div>
          </div>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">V. Hướng dẫn sử dụng VPN trên Android/PC</h2>
          
          <div class="space-y-8">
            <div class="bg-orange-50 p-6 rounded-lg">
              <h3 class="font-semibold text-orange-800 mb-4">📱 A. Trên Android</h3>
              
              <h4 class="font-semibold mb-3">Bước 1: Tải ứng dụng VPN</h4>
              <ul class="space-y-1 mb-4">
                <li>• Tải ứng dụng VPN từ Google Play Store</li>
                <li>• <strong>Ví dụ:</strong> Windscribe, ProtonVPN</li>
              </ul>

              <h4 class="font-semibold mb-3">Bước 2: Đăng ký tài khoản miễn phí</h4>
              <ul class="space-y-1 mb-4">
                <li>• Cung cấp email (nếu cần) để nhận dung lượng miễn phí</li>
                <li>• Tạo tài khoản và xác thực email</li>
              </ul>

              <h4 class="font-semibold mb-3">Bước 3: Kết nối VPN</h4>
              <ol class="space-y-2 mb-4">
                <li><strong>1.</strong> Mở ứng dụng → Đăng nhập</li>
                <li><strong>2.</strong> Chọn máy chủ từ quốc gia mong muốn (ví dụ: Mỹ, Nhật, Đức)</li>
                <li><strong>3.</strong> Nhấn <strong>Connect</strong></li>
              </ol>

              <h4 class="font-semibold mb-3">Bước 4: Kiểm tra IP</h4>
              <ul class="space-y-1">
                <li>• Vào <strong>WhatIsMyIP</strong> để kiểm tra địa chỉ IP mới</li>
                <li>• Nếu IP khác với IP gốc → VPN hoạt động thành công</li>
              </ul>
            </div>

            <div class="bg-blue-50 p-6 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-4">💻 B. Trên PC (Windows/Mac)</h3>
              
              <h4 class="font-semibold mb-3">Bước 1: Tải ứng dụng VPN</h4>
              <ul class="space-y-1 mb-4">
                <li>• Truy cập trang chính thức của Windscribe hoặc ProtonVPN</li>
                <li>• Tải phần mềm phù hợp với hệ điều hành</li>
              </ul>

              <h4 class="font-semibold mb-3">Bước 2: Cài đặt và đăng nhập</h4>
              <ul class="space-y-1 mb-4">
                <li>• Cài đặt ứng dụng</li>
                <li>• Đăng nhập bằng tài khoản đã tạo</li>
              </ul>

              <h4 class="font-semibold mb-3">Bước 3: Chọn máy chủ</h4>
              <ul class="space-y-1 mb-4">
                <li>• Chọn quốc gia bạn muốn đổi IP (ví dụ: "US - Free")</li>
                <li>• Nhấn <strong>Connect</strong></li>
              </ul>

              <h4 class="font-semibold mb-3">Bước 4: Kiểm tra IP</h4>
              <ul class="space-y-1">
                <li>• Truy cập <strong>WhatIsMyIP</strong> trên trình duyệt</li>
                <li>• Kiểm tra địa chỉ IP mới</li>
              </ul>
            </div>
          </div>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">VI. Hướng dẫn chọn VPN tốt</h2>
          
          <div class="bg-gray-50 p-4 rounded-lg my-6">
            <ul class="space-y-2">
              <li>• <strong>Không lưu log:</strong> Không ghi lại hoạt động của người dùng</li>
              <li>• <strong>Mã hóa mạnh:</strong> Sử dụng AES-256 hoặc tương đương</li>
              <li>• <strong>Nhiều server:</strong> Có server ở nhiều quốc gia</li>
              <li>• <strong>Tốc độ cao:</strong> Không làm chậm kết nối internet</li>
              <li>• <strong>Hỗ trợ đa nền tảng:</strong> Windows, Mac, iOS, Android</li>
              <li>• <strong>Giá cả hợp lý:</strong> Phù hợp với ngân sách</li>
            </ul>
          </div>

          <h2 class="mt-4 mb-2 text-3xl font-bold text-gray-900">VII. Lưu ý quan trọng</h2>
          
          <div class="bg-red-50 p-4 rounded-lg my-6">
            <ul class="space-y-2 text-red-700">
              <li>• <strong>Không phải tất cả VPN đều an toàn 100%</strong> - cần chọn nhà cung cấp uy tín</li>
              <li>• <strong>Đọc kỹ chính sách bảo mật</strong> trước khi sử dụng</li>
              <li>• <strong>Không sử dụng VPN miễn phí cho dữ liệu nhạy cảm</strong> - ngân hàng, thanh toán</li>
              <li>• <strong>VPN có thể làm chậm tốc độ internet</strong> - chọn server gần nhất</li>
              <li>• <strong>Một số website có thể chặn VPN</strong> - cần thử server khác</li>
            </ul>
          </div>

          <h2 class="text-3xl font-bold text-gray-900">VIII. Kết luận</h2>
          <p>
            VPN là công cụ thiết yếu trong thời đại số, giúp bảo vệ dữ liệu cá nhân và 
            tăng cường quyền riêng tư trực tuyến. Việc lựa chọn và sử dụng VPN đúng cách 
            sẽ mang lại nhiều lợi ích cho cuộc sống số của bạn.
          </p>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const title = 'VPN - Bảo mật mạng: Hướng dẫn sử dụng VPN 2024'
const desc = 'Hướng dẫn sử dụng VPN để bảo vệ dữ liệu cá nhân, truy cập an toàn và vượt qua các hạn chế địa lý. Tìm hiểu VPN là gì và cách hoạt động'
const url = 'https://webservices.vn/blog/vpn-bao-mat-mang'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
